import type { AppRouter } from "@runcheetah/api/router";
import type { inferRouterInputs } from "@trpc/server";
import type { UIMessage } from "ai";
import { Check, ChevronDown, ChevronRight, Loader2, WandSparkles } from "lucide-react";
import { useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "sonner";

import type {
  AssistantBrandProfileEditPreviewChange,
  AssistantBrandProfileEditPreviewReference,
  AssistantBrandProfileEditPreviewResolutionStatus,
  AssistantBrandProfileEditPreviewValue,
} from "@/lib/assistant-brand-profile-edit-previews";
import {
  buildAssistantBrandProfileEditPreviewDigest,
  formatStructuredObjectPreviewLabel,
} from "@/lib/assistant-brand-profile-edit-previews";
import { trpc } from "@/lib/trpc";
import { Button } from "@runcheetah/ui";

type RouterInputs = inferRouterInputs<AppRouter>;
type UpdateBrandVoiceInput = RouterInputs["brand"]["updateBrandVoice"];
type OnboardingPreviewResolution = {
  previewId: string;
  status: AssistantBrandProfileEditPreviewResolutionStatus;
  messages: UIMessage[] | null;
};

const ONBOARDING_SUMMARY_MAX_CHANGES = 5;
const ONBOARDING_SUMMARY_FIELD_PRIORITIES: readonly RegExp[] = [
  /\b(positioning|market category|market subcategory|unique selling proposition)\b/i,
  /\b(primary audience|demographics|audience segment|psychographics)\b/i,
  /\b(key differentiators?|competitive advantages?|differentiators?)\b/i,
  /\b(competitors?|substitutes?)\b/i,
  /\b(voice|tone|vocabulary)\b/i,
];

export function AssistantBrandProfileEditPreviews({
  brandId,
  chatId,
  references,
  resolutionStatuses,
  onOnboardingPreviewResolved,
}: {
  brandId: string;
  chatId?: string | null;
  references: AssistantBrandProfileEditPreviewReference[];
  resolutionStatuses?: ReadonlyMap<string, AssistantBrandProfileEditPreviewResolutionStatus>;
  onOnboardingPreviewResolved?: (resolution: OnboardingPreviewResolution) => void;
}) {
  if (references.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {references.map((reference) => (
        <AssistantBrandProfileEditPreviewCard
          key={reference.id}
          brandId={brandId}
          chatId={chatId}
          reference={reference}
          resolvedStatus={resolutionStatuses?.get(reference.id) ?? null}
          onOnboardingPreviewResolved={onOnboardingPreviewResolved}
        />
      ))}
    </div>
  );
}

function AssistantBrandProfileEditPreviewCard({
  brandId,
  chatId,
  reference,
  resolvedStatus,
  onOnboardingPreviewResolved,
}: {
  brandId: string;
  chatId?: string | null;
  reference: AssistantBrandProfileEditPreviewReference;
  resolvedStatus: AssistantBrandProfileEditPreviewResolutionStatus | null;
  onOnboardingPreviewResolved?: (resolution: OnboardingPreviewResolution) => void;
}) {
  const intl = useIntl();
  const utils = trpc.useUtils();
  const isOnboardingDraft = reference.confirmationSource === "chatbot_onboarding";
  const [localResolution, setLocalResolution] =
    useState<AssistantBrandProfileEditPreviewResolutionStatus | null>(null);
  const [expanded, setExpanded] = useState(() => reference.changes.length <= 2);
  const [showOnboardingDiff, setShowOnboardingDiff] = useState(false);
  const digest = buildAssistantBrandProfileEditPreviewDigest(reference);
  const resolution = localResolution ?? resolvedStatus;
  const applied = resolution === "applied";
  const dismissed = resolution === "dismissed";
  const mutation = trpc.brand.updateBrandVoice.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.brand.getBrandProfile.invalidate({ brandId }),
        utils.brand.getBrandVoice.invalidate({ brandId }),
        utils.brand.getVisualStyle.invalidate({ brandId }),
        utils.brand.getStrategyRegenerationStatus.invalidate({ brandId }),
      ]);
    },
  });
  const resolvePreviewMutation =
    trpc.brandAssistantChat.resolveOnboardingProfilePreview.useMutation({
      onSuccess: (data) => {
        if (chatId && data.chat) {
          utils.brandAssistantChat.get.setData({ brandId, chatId }, { chat: data.chat });
        }

        void Promise.all([
          chatId ? utils.brandAssistantChat.get.invalidate({ brandId, chatId }) : Promise.resolve(),
          utils.brandAssistantChat.list.invalidate({ brandId }),
          utils.brand.getById.invalidate({ brandId }),
        ]);
      },
    });

  const persistPreviewResolution = async (
    status: AssistantBrandProfileEditPreviewResolutionStatus,
  ) => {
    if (!chatId) {
      return null;
    }

    const data = await resolvePreviewMutation.mutateAsync({
      brandId,
      chatId,
      previewId: reference.id,
      status,
    });

    return data.chat?.messages ? (data.chat.messages as unknown as UIMessage[]) : null;
  };

  const applyChanges = async () => {
    if (
      (reference.changes.length === 0 && (reference.audienceProfiles?.length ?? 0) === 0) ||
      mutation.isPending ||
      resolvePreviewMutation.isPending ||
      applied ||
      dismissed
    ) {
      return;
    }

    const fields = Object.fromEntries(
      reference.changes.map((change) => [change.fieldPath, change.next]),
    ) as UpdateBrandVoiceInput["fields"];
    const expectedPreviousFields = Object.fromEntries(
      reference.changes.map((change) => [change.fieldPath, change.previous]),
    ) as UpdateBrandVoiceInput["expectedPreviousFields"];
    const sourceMetadataEntries: [
      string,
      NonNullable<UpdateBrandVoiceInput["sourceMetadata"]>[string],
    ][] = reference.changes.flatMap((change) => {
      const sourceMetadata: NonNullable<UpdateBrandVoiceInput["sourceMetadata"]>[string] = {};

      if ("sourceUrl" in change) {
        sourceMetadata.sourceUrl = change.sourceUrl ?? null;
      }

      if ("sourceType" in change) {
        sourceMetadata.sourceType = change.sourceType ?? null;
      }

      if ("confidence" in change) {
        sourceMetadata.confidence = change.confidence ?? null;
      }

      return Object.keys(sourceMetadata).length > 0
        ? ([[change.fieldPath, sourceMetadata]] as [
            string,
            NonNullable<UpdateBrandVoiceInput["sourceMetadata"]>[string],
          ][])
        : [];
    });
    const sourceMetadata =
      sourceMetadataEntries.length > 0
        ? (Object.fromEntries(sourceMetadataEntries) as UpdateBrandVoiceInput["sourceMetadata"])
        : undefined;

    try {
      await mutation.mutateAsync({
        brandId,
        fields,
        expectedPreviousFields: isOnboardingDraft ? undefined : expectedPreviousFields,
        confirmationSource: reference.confirmationSource,
        sourceMetadata,
        audienceProfiles: reference.audienceProfiles,
      });
      const resolvedMessages = await persistPreviewResolution("applied");
      onOnboardingPreviewResolved?.({
        previewId: reference.id,
        status: "applied",
        messages: resolvedMessages,
      });
      setLocalResolution("applied");
      if ((reference.audienceProfiles?.length ?? 0) > 0) {
        void utils.audienceProfile.list.invalidate({ brandId });
      }
      toast.success(
        intl.formatMessage({
          id: "assistantBrandProfileEditPreview.applySuccess",
          defaultMessage: "Brand profile updated",
        }),
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : intl.formatMessage({
              id: "common.couldNotSaveChanges",
              defaultMessage: "Could not save changes",
            }),
      );
    }
  };

  const title = intl.formatMessage({
    id: "assistantBrandProfileEditPreview.title",
    defaultMessage: "Brand profile",
  });
  const defaultDescription = intl.formatMessage({
    id: "assistantBrandProfileEditPreview.description",
    defaultMessage: "Review the suggested fact changes before applying them.",
  });
  const description = isOnboardingDraft
    ? sanitizeOnboardingPreviewSummary(reference.summary) || defaultDescription
    : reference.summary || defaultDescription;
  const isResolving = mutation.isPending || resolvePreviewMutation.isPending;
  const hasApplicableContent =
    reference.changes.length > 0 || (reference.audienceProfiles?.length ?? 0) > 0;
  const canApply = hasApplicableContent && !isResolving && !applied && !dismissed;

  if (isOnboardingDraft) {
    const summaryChanges = getOnboardingPreviewSummaryChanges(reference.changes);

    return (
      <div className="motion-safe:animate-[assistant-memory-fade-in_220ms_ease-out]">
        <div className="max-w-[42rem] space-y-3 rounded-2xl border border-border/60 bg-card/55 px-4 py-3 shadow-[0_12px_36px_-30px_rgba(19,34,49,0.55)]">
          <div className="text-sm font-semibold leading-5 text-foreground">
            <FormattedMessage
              id="assistantBrandProfileEditPreview.onboardingTitle"
              defaultMessage="Brand Profile preview"
            />
          </div>

          {!hasApplicableContent ? (
            <p className="text-sm leading-6 text-muted-foreground">
              <FormattedMessage
                id="assistantBrandProfileEditPreview.noChanges"
                defaultMessage="No safe profile fact changes were found in this request."
              />
            </p>
          ) : reference.changes.length > 0 ? (
            <OnboardingPlainFactList changes={summaryChanges} />
          ) : null}

          {reference.audienceProfiles && reference.audienceProfiles.length > 0 ? (
            <OnboardingAudienceProfileList profiles={reference.audienceProfiles} />
          ) : null}

          {hasApplicableContent ? (
            <OnboardingPreviewActionRow
              applied={applied}
              canApply={canApply}
              dismissed={dismissed}
              isPending={isResolving}
              showDiff={showOnboardingDiff}
              onApply={() => void applyChanges()}
              onToggleDiff={() => setShowOnboardingDiff((current) => !current)}
            />
          ) : null}

          {showOnboardingDiff && reference.changes.length > 0 ? (
            <>
              <OnboardingPreviewDetailsList changes={reference.changes} />
              <BottomCollapseButton
                label={
                  <FormattedMessage
                    id="assistantBrandProfileEditPreview.onboardingHideDiff"
                    defaultMessage="Hide details"
                  />
                }
                onClick={() => setShowOnboardingDiff(false)}
              />
            </>
          ) : null}

          <p className="text-sm leading-6 text-muted-foreground">
            <FormattedMessage
              id="assistantBrandProfileEditPreview.onboardingCorrectionHint"
              defaultMessage="You can also tell me what to change."
            />
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="border-l-2 border-info/35 py-1 pl-3.5">
      <div className="space-y-2.5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-1">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                <WandSparkles className="h-3.5 w-3.5 text-info" aria-hidden="true" />
                {title}
              </span>
              <span className="text-muted-foreground/42" aria-hidden="true">
                ·
              </span>
              <span className="text-xs font-medium text-muted-foreground">
                <FormattedMessage
                  id="assistantBrandProfileEditPreview.changeCount"
                  defaultMessage="{count, plural, one {# fact} other {# facts}}"
                  values={{ count: reference.changes.length }}
                />
              </span>
            </div>
            <p className="text-sm leading-6 text-foreground/72">{description}</p>
          </div>
        </div>

        {reference.changes.length === 0 ? (
          <p className="text-sm leading-6 text-muted-foreground">
            <FormattedMessage
              id="assistantBrandProfileEditPreview.noChanges"
              defaultMessage="No safe profile fact changes were found in this request."
            />
          </p>
        ) : (
          <PreviewHighlightList digest={digest} />
        )}

        {reference.changes.length > 0 ? (
          <PreviewActionRow
            applied={applied}
            canApply={canApply}
            dismissed={dismissed}
            expanded={expanded}
            isPending={isResolving}
            onApply={() => void applyChanges()}
            onToggle={() => setExpanded((current) => !current)}
          />
        ) : null}

        {expanded && reference.changes.length > 0 ? (
          <>
            <div className="divide-y divide-border/55 pt-1">
              {reference.changes.map((change) => (
                <PreviewChangeRow key={change.fieldPath} change={change} />
              ))}
            </div>
            <BottomCollapseButton
              label={
                <FormattedMessage
                  id="assistantBrandProfileEditPreview.hideDetails"
                  defaultMessage="Hide details"
                />
              }
              onClick={() => setExpanded(false)}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}

export function OnboardingAudienceProfileList({
  profiles,
}: {
  profiles: NonNullable<AssistantBrandProfileEditPreviewReference["audienceProfiles"]>;
}) {
  return (
    <div className="space-y-2 border-t border-border/50 pt-3">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        <FormattedMessage
          id="assistantBrandProfileEditPreview.onboardingAudienceProfiles"
          defaultMessage="Audience profiles"
        />
      </p>
      <div className="space-y-2">
        {profiles.map((profile) => (
          <div key={profile.name} className="space-y-0.5">
            <p className="text-sm font-medium leading-5 text-foreground">{profile.name}</p>
            <p className="text-xs leading-5 text-muted-foreground">{profile.summary}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function BottomCollapseButton({ label, onClick }: { label: ReactNode; onClick: () => void }) {
  return (
    <div className="pt-1">
      <button
        type="button"
        className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        onClick={onClick}
        aria-expanded={true}
      >
        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
        {label}
      </button>
    </div>
  );
}

function ApplyPreviewButton({
  applied,
  canApply,
  dismissed,
  isPending,
  mode = "default",
  onApply,
}: {
  applied: boolean;
  canApply: boolean;
  dismissed: boolean;
  isPending: boolean;
  mode?: "default" | "onboarding";
  onApply: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      className="h-8 shrink-0 rounded-full px-3 text-xs font-semibold"
      disabled={!canApply}
      onClick={onApply}
    >
      <ApplyPreviewButtonContent
        applied={applied}
        dismissed={dismissed}
        isPending={isPending}
        mode={mode}
      />
    </Button>
  );
}

function ApplyPreviewButtonContent({
  applied,
  dismissed,
  isPending,
  mode = "default",
}: {
  applied: boolean;
  dismissed: boolean;
  isPending: boolean;
  mode?: "default" | "onboarding";
}) {
  if (isPending) {
    if (mode === "onboarding") {
      return (
        <>
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          <FormattedMessage
            id="assistantBrandProfileEditPreview.onboardingSaving"
            defaultMessage="Saving..."
          />
        </>
      );
    }

    return (
      <>
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        <FormattedMessage
          id="assistantBrandProfileEditPreview.applying"
          defaultMessage="Applying..."
        />
      </>
    );
  }

  if (applied) {
    if (mode === "onboarding") {
      return (
        <>
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
          <FormattedMessage
            id="assistantBrandProfileEditPreview.onboardingApplied"
            defaultMessage="Saved to Brand Profile"
          />
        </>
      );
    }

    return (
      <>
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
        <FormattedMessage id="assistantBrandProfileEditPreview.applied" defaultMessage="Applied" />
      </>
    );
  }

  if (dismissed) {
    return (
      <>
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
        <FormattedMessage id="assistantBrandProfileEditPreview.skipped" defaultMessage="Skipped" />
      </>
    );
  }

  if (mode === "onboarding") {
    return (
      <FormattedMessage
        id="assistantBrandProfileEditPreview.onboardingApply"
        defaultMessage="Save to Brand Profile"
      />
    );
  }

  return <FormattedMessage id="assistantBrandProfileEditPreview.apply" defaultMessage="Apply" />;
}

function OnboardingPlainFactList({
  changes,
}: {
  changes: AssistantBrandProfileEditPreviewChange[];
}) {
  return (
    <div className="space-y-2">
      {changes.map((change) => (
        <OnboardingPlainFact key={change.fieldPath} change={change} />
      ))}
    </div>
  );
}

function OnboardingPlainFact({ change }: { change: AssistantBrandProfileEditPreviewChange }) {
  return (
    <div className="grid gap-1 text-sm leading-6 sm:grid-cols-[9.5rem_minmax(0,1fr)]">
      <div className="font-semibold text-foreground/72">{change.label}</div>
      <div className="text-foreground/82">
        <FormattedPreviewValue value={change.next} />
      </div>
    </div>
  );
}

export function OnboardingPreviewDetailsList({
  changes,
}: {
  changes: AssistantBrandProfileEditPreviewChange[];
}) {
  return (
    <div className="space-y-2 pt-1">
      {changes.map((change) => (
        <OnboardingPlainFact key={change.fieldPath} change={change} />
      ))}
    </div>
  );
}

export function getOnboardingPreviewSummaryChanges(
  changes: readonly AssistantBrandProfileEditPreviewChange[],
): AssistantBrandProfileEditPreviewChange[] {
  const selected: AssistantBrandProfileEditPreviewChange[] = [];
  const selectedPaths = new Set<string>();

  for (const priority of ONBOARDING_SUMMARY_FIELD_PRIORITIES) {
    const match = changes.find(
      (change) =>
        !selectedPaths.has(change.fieldPath) && priority.test(getChangeSearchText(change)),
    );
    if (match) {
      selected.push(match);
      selectedPaths.add(match.fieldPath);
    }
  }

  for (const change of changes) {
    if (selected.length >= ONBOARDING_SUMMARY_MAX_CHANGES) {
      break;
    }

    if (!selectedPaths.has(change.fieldPath)) {
      selected.push(change);
      selectedPaths.add(change.fieldPath);
    }
  }

  return selected.slice(0, ONBOARDING_SUMMARY_MAX_CHANGES);
}

function getChangeSearchText(change: AssistantBrandProfileEditPreviewChange) {
  return `${change.fieldPath} ${change.label}`;
}

function PreviewHighlightList({
  digest,
}: {
  digest: ReturnType<typeof buildAssistantBrandProfileEditPreviewDigest>;
}) {
  return (
    <ul className="space-y-1 text-sm leading-6 text-foreground/78">
      {digest.highlights.map((highlight) => (
        <li key={highlight} className="flex gap-2">
          <span className="mt-[0.7rem] h-1 w-1 shrink-0 rounded-full bg-info/70" />
          <span>{highlight}</span>
        </li>
      ))}
      {digest.hiddenChangeCount > 0 ? (
        <li className="pl-3 text-xs font-medium text-muted-foreground">
          <FormattedMessage
            id="assistantBrandProfileEditPreview.moreChanges"
            defaultMessage="+ {count, plural, one {# more change} other {# more changes}}"
            values={{ count: digest.hiddenChangeCount }}
          />
        </li>
      ) : null}
    </ul>
  );
}

export function sanitizeOnboardingPreviewSummary(summary: string | null): string | null {
  const normalized = summary?.replace(/\s+/g, " ").trim();
  if (!normalized) {
    return null;
  }

  const sentences = normalized.match(/[^.!?]+[.!?]+|[^.!?]+$/g) ?? [normalized];
  const visibleSentences = sentences
    .map((sentence) => sentence.trim())
    .filter((sentence) => sentence && !isInternalOnboardingSummarySentence(sentence));

  return visibleSentences.join(" ").trim() || null;
}

function isInternalOnboardingSummarySentence(sentence: string) {
  const normalized = sentence.toLowerCase();
  return (
    normalized.includes("unsupported field") ||
    normalized.includes("not allowed field") ||
    normalized.includes("allowed fields") ||
    normalized.includes("not strongly grounded") ||
    normalized.includes("safe profile fact") ||
    normalized.includes("excluded unsupported") ||
    normalized.includes("excluded fields") ||
    normalized.includes("not grounded enough")
  );
}

function PreviewActionRow({
  applied,
  canApply,
  dismissed,
  expanded,
  isPending,
  onApply,
  onToggle,
}: {
  applied: boolean;
  canApply: boolean;
  dismissed: boolean;
  expanded: boolean;
  isPending: boolean;
  onApply: () => void;
  onToggle: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pt-0.5 text-xs font-medium">
      <ApplyPreviewButton
        applied={applied}
        canApply={canApply}
        dismissed={dismissed}
        isPending={isPending}
        onApply={onApply}
      />
      <button
        type="button"
        className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        {expanded ? (
          <FormattedMessage
            id="assistantBrandProfileEditPreview.hideDetails"
            defaultMessage="Hide details"
          />
        ) : (
          <FormattedMessage
            id="assistantBrandProfileEditPreview.showDetails"
            defaultMessage="Show full diff"
          />
        )}
      </button>
    </div>
  );
}

function OnboardingPreviewActionRow({
  applied,
  canApply,
  dismissed,
  isPending,
  showDiff,
  onApply,
  onToggleDiff,
}: {
  applied: boolean;
  canApply: boolean;
  dismissed: boolean;
  isPending: boolean;
  showDiff: boolean;
  onApply: () => void;
  onToggleDiff: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pt-0.5 text-xs font-medium">
      <Button
        type="button"
        size="sm"
        className="h-7 rounded-full px-2.5 text-xs"
        disabled={!canApply}
        onClick={onApply}
      >
        <ApplyPreviewButtonContent
          applied={applied}
          dismissed={dismissed}
          isPending={isPending}
          mode="onboarding"
        />
      </Button>
      <button
        type="button"
        className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
        onClick={onToggleDiff}
        aria-expanded={showDiff}
      >
        {showDiff ? (
          <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        {showDiff ? (
          <FormattedMessage
            id="assistantBrandProfileEditPreview.onboardingHideDiff"
            defaultMessage="Hide details"
          />
        ) : (
          <FormattedMessage
            id="assistantBrandProfileEditPreview.onboardingShowDiff"
            defaultMessage="Show details"
          />
        )}
      </button>
    </div>
  );
}

function PreviewChangeRow({ change }: { change: AssistantBrandProfileEditPreviewChange }) {
  return (
    <div className="py-2.5 text-sm leading-6">
      <div className="min-w-0">
        <span className="font-semibold text-foreground">{change.label}</span>
      </div>
      <div className="mt-1 grid gap-1 text-foreground/78 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-start">
        <div className="min-w-0 text-muted-foreground line-through decoration-muted-foreground/45 decoration-1">
          <FormattedPreviewValue value={change.previous} />
        </div>
        <span className="hidden px-1 text-muted-foreground/45 sm:block" aria-hidden="true">
          →
        </span>
        <div className="min-w-0 font-medium text-foreground">
          <FormattedPreviewValue value={change.next} />
        </div>
      </div>
    </div>
  );
}

function FormattedPreviewValue({ value }: { value: AssistantBrandProfileEditPreviewValue | null }) {
  if (value === null || (typeof value === "string" && value.trim().length === 0)) {
    return <FormattedMessage id="common.notSet" defaultMessage="Not set" />;
  }

  if (typeof value === "string") {
    return <span className="whitespace-pre-wrap">{value}</span>;
  }

  if (value.length === 0) {
    return (
      <FormattedMessage
        id="assistantBrandProfileEditPreview.emptyList"
        defaultMessage="Empty list"
      />
    );
  }

  return (
    <ul className="space-y-1">
      {value.map((item, index) => (
        <li key={getPreviewValueItemKey(item, index)} className="flex gap-2 leading-5">
          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-current opacity-45" />
          <span>{formatPreviewValueItem(item)}</span>
        </li>
      ))}
    </ul>
  );
}
type PreviewValueItem = string | { name?: unknown; label?: unknown } | [string, string];

function getPreviewValueItemKey(value: PreviewValueItem, index: number) {
  if (typeof value === "string") {
    return `${value}-${index}`;
  }

  if (isCompetitorTuplePreviewValue(value)) {
    return `${value[0]}-${value[1]}-${index}`;
  }

  return `${formatStructuredObjectPreviewLabel(value)}-${index}`;
}

function formatPreviewValueItem(value: PreviewValueItem) {
  if (typeof value === "string") {
    return value;
  }

  if (!isCompetitorTuplePreviewValue(value)) {
    return formatStructuredObjectPreviewLabel(value);
  }

  const [name, url] = value;

  return (
    <span className="min-w-0">
      <a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline">
        {name}
      </a>
      <span className="ml-1 text-xs text-muted-foreground">{formatPreviewUrlHost(url)}</span>
    </span>
  );
}

function isCompetitorTuplePreviewValue(value: unknown): value is [string, string] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === "string" &&
    typeof value[1] === "string"
  );
}

function formatPreviewUrlHost(url: string) {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}
